import { useState, type FormEvent } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2 } from "lucide-react";
import InfoTip from "@/components/info-tip";

export default function FounderCheckin({ founderId }: { founderId: number }) {
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);

    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/founder/${founderId}/checkin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          headline: form.get("headline"),
          highlights: form.get("highlights") || null,
          challenges: form.get("challenges") || null,
          asks: form.get("asks") || null,
          metrics: form.get("metrics") || null,
        }),
      });
      if (!res.ok) throw new Error("Failed to submit check-in");
      setSubmitted(true);
      toast({ title: "Check-in submitted", description: "Your weekly update has been recorded." });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center space-y-4" data-testid="page-founder-checkin-success">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h2 className="text-xl font-serif font-bold">Check-in Submitted</h2>
        <p className="text-sm text-muted-foreground max-w-md">
          Your weekly update has been submitted. The Favcy team will review it and may promote key items for investor visibility.
        </p>
        <Button variant="outline" onClick={() => setSubmitted(false)}>Submit Another</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="page-founder-checkin">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-serif font-bold tracking-tight">Weekly Check-in</h1>
          <InfoTip content="This is your weekly update to the Favcy team. Fill in a quick headline about your week, key wins, any blockers, and if you need help with anything. The ops team reviews these and may promote highlights into investor-facing updates — but only with your approval." />
        </div>
        <p className="text-sm text-muted-foreground mt-1">Share your progress, challenges, and what you need this week</p>
      </div>

      <Card>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="headline" className="text-sm font-medium">Headline *</Label>
              <p className="text-xs text-muted-foreground mb-2">One sentence summary of your week</p>
              <Input id="headline" name="headline" placeholder="e.g. Closed first enterprise deal, MRR crossed $50K" required />
            </div>

            <div>
              <Label htmlFor="highlights" className="text-sm font-medium">Highlights</Label>
              <p className="text-xs text-muted-foreground mb-2">Key wins and progress this week</p>
              <Textarea id="highlights" name="highlights" rows={3} placeholder="What went well?" />
            </div>

            <div>
              <Label htmlFor="challenges" className="text-sm font-medium">Challenges</Label>
              <p className="text-xs text-muted-foreground mb-2">What's blocking you or slowing you down?</p>
              <Textarea id="challenges" name="challenges" rows={3} placeholder="What are you struggling with?" />
            </div>

            <div>
              <Label htmlFor="asks" className="text-sm font-medium">Asks</Label>
              <p className="text-xs text-muted-foreground mb-2">How can the Favcy team help you this week?</p>
              <Textarea id="asks" name="asks" rows={3} placeholder="Intros needed, resources, advice..." />
            </div>

            <div>
              <Label htmlFor="metrics" className="text-sm font-medium">Key Metrics</Label>
              <p className="text-xs text-muted-foreground mb-2">Any numbers worth highlighting</p>
              <Textarea id="metrics" name="metrics" rows={2} placeholder="MRR, users, pipeline, burn rate..." />
            </div>

            <Button type="submit" className="w-full">Submit Check-in</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
